import {
  AfterViewChecked,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  effect,
  inject,
  input as signalInput,
} from '@angular/core';
import {
  Circle,
  CircleMarker,
  ErrorEvent,
  Icon,
  LatLngBounds,
  Layer,
  LocationEvent,
  Map,
  Marker,
  MapOptions,
  Polyline,
  circle,
  latLng,
  latLngBounds,
  marker,
} from 'leaflet';
import { TileLayerOffline, tileLayerOffline } from 'leaflet.offline';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { isTopLevelDomainFI } from '../../../shared/util/deploy-helper';
import { SelectSpanModeService } from '../../service/map/span/select-span-mode/select-span-mode.service';
import { SpanService } from '../../service/map/span/span.service';
import { PointType, iconTypeSizes } from '../../span-progress-entities';
import { DuctInteractionService } from '../duct-table/services/duct-interaction.service';
import { SidePanel } from '../map-controls/map-controls.component';
import { MapInteractionService } from '../map-interaction/map-interaction.service';
import {
  downloadTilesForOffline,
  shouldDownloadOfflineTiles,
} from './offline-map.helper';

const fastighetplusUrl = `${environment.mapproxyUrl}`;

const openStreetUrl = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const openStreetAttr =
  'Map data &copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>';
export interface TileStatus {
  tilesSaved: number;
  tilesToSave: number;
  done: boolean;
}
@Component({
  selector: 'app-map',
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  standalone: false,
})
export class MapComponent implements OnInit, OnDestroy, AfterViewChecked {
  private readonly selectSpanModeService = inject(SelectSpanModeService);

  @Input() layers: Layer[] = [];
  @Input() overlay = {};
  @Input() mapBounds: LatLngBounds;
  @Input() activeSidePanel: SidePanel | null = null;

  version = signalInput<string>('');

  @Output() geolocationDenied = new EventEmitter();
  @Output() sidePanelSelected = new EventEmitter<SidePanel>();
  @Output() mapReady = new EventEmitter<void>();

  cancelSub$ = new Subject<void>();
  map: Map;
  mapLoaded = false;
  showGeolocation = false;
  gpsMarker: Circle | null = null;
  gpsDotMarker: Marker | null = null;
  zoomLevelsToSave = [11, 12, 13, 14, 15, 16, 17, 18];
  mapOptions: MapOptions = {};

  allSpans = this.spanService.getLayerData()?.getLayers();

  isDeliverPortalFi = isTopLevelDomainFI();

  baseLayerOffline: TileLayerOffline = tileLayerOffline(fastighetplusUrl, {
    id: 'fastighetplus',
    tileSize: 256,
    maxZoom: 24,
    minZoom: 0,
    attribution: '',
  });

  openStreetMapLayerOffline: TileLayerOffline = tileLayerOffline(
    openStreetUrl,
    {
      tileSize: 256,
      maxZoom: 24,
      minZoom: 0,
      attribution: openStreetAttr,
    },
  );

  geolocationImage = new Icon({
    iconUrl: './assets/leaflet/images/geolocation-point.svg',
    iconSize: [24, 24],
    iconAnchor: [12, 12],
    shadowUrl: './assets/leaflet/images/geolocation-point-shadow.png',
    shadowSize: [32, 32],
    shadowAnchor: [16, 16],
    className: 'geolocation',
  });

  readonly isMultiSpanHelpModalOpen =
    this.selectSpanModeService.isMultiSpanHelpModalOpen;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly ductInteractionService: DuctInteractionService,
    private readonly spanService: SpanService,
    private readonly mapInterActionService: MapInteractionService,
  ) {
    effect(() => {
      this.handleMapInteractionForModal();
    });
  }

  ngOnInit() {
    this.ductInteractionService
      .getSelectedDuct$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((selectedDuct) => {
        let spanArray: Layer[] = [];

        if (selectedDuct.duct) {
          spanArray =
            this.spanService.getSpanLayersBySpanIds(
              selectedDuct?.duct.spanIds,
            ) ?? [];
          const bounds = this.ductInteractionService.calculateFeatureBounds(
            spanArray as Polyline[],
          );
          if (bounds) {
            this.centerViewToObject(bounds);
          }
        }
      });
    this.mapInterActionService
      .getSelectedResultControl$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((selectedSearchItem) => {
        if (selectedSearchItem.clickedObject?.getLatLng()) {
          const bounds = latLngBounds([
            selectedSearchItem.clickedObject.getLatLng(),
          ]);
          this.centerViewToObject(bounds);
        }
      });
    this.mapInterActionService
      .getSelectedSpanControl$()
      .pipe(takeUntil(this.cancelSub$))
      .subscribe((span) => {
        if (span) {
          this.centerViewToObject(span.getBounds());
        }
      });
  }

  ngOnDestroy() {
    this.cancelSub$.next();
  }

  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }

  onMapReady(map: Map) {
    this.map = map;
    this.map.addLayer(this.openStreetMapLayerOffline);
    !this.isDeliverPortalFi && this.map.addLayer(this.baseLayerOffline);

    this.layers.forEach((layer: Layer) => {
      this.map.addLayer(layer);
    });

    map.on('locationerror', (e) => this.onGeolocationError(e));
    map.on('locationfound', (e) => this.onGeolocationFound(e));

    this.centerView();
    this.showMapControls(true);
    if (
      this.mapBounds &&
      this.allSpans &&
      shouldDownloadOfflineTiles(
        this.map,
        this.mapBounds,
        this.zoomLevelsToSave,
      )
    ) {
      downloadTilesForOffline(
        this.zoomLevelsToSave,
        this.map,
        this.openStreetMapLayerOffline,
        this.mapBounds,
        this.allSpans,
      );
    }

    this.map.setZoom(this.map.getZoom());
    map.on('zoomend', () => this.changePointSizeOnZoom(map.getZoom(), map));
    this.mapReady.emit();
  }

  changePointSizeOnZoom(zoomLevel: number, map: Map) {
    if (zoomLevel > 18) {
      this.changePointRadius(map, true);
    } else {
      this.changePointRadius(map, false);
    }
  }

  changePointRadius(map: Map, zoomedIn: boolean) {
    const standardRadius = 16;
    const radius = this.calculateRadiusBasedOnZoom(standardRadius, zoomedIn);
    map.eachLayer((layer) => {
      if (layer instanceof CircleMarker) layer.setRadius(radius);
      if (layer instanceof Marker) {
        this.changeIconRadius(layer, zoomedIn, standardRadius);
      }
    });
  }

  changeIconRadius(
    layer: Marker<any>,
    zoomedIn: boolean,
    standardRadius: number,
  ) {
    const iconRadius =
      this.getIconRadius(layer, zoomedIn) ??
      this.calculateRadiusBasedOnZoom(standardRadius, zoomedIn);
    const icon = layer.getIcon();
    icon.options.iconSize = [iconRadius * 2, iconRadius * 2];
    layer.setIcon(icon);
  }

  getIconRadius(layer: Marker<any>, zoomedIn: boolean) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    switch (layer.feature?.properties.markerTypeId) {
      case PointType.Hub:
        return this.calculateIconRadius('hub', zoomedIn, true);
      case PointType.Site:
        return this.calculateIconRadius('default', zoomedIn);
      case PointType.Cabinet:
        return this.calculateIconRadius('default', zoomedIn);
      case PointType.MultiHC:
        return this.calculateIconRadius('multi', zoomedIn);
      case PointType.MultiHP:
        return this.calculateIconRadius('multi', zoomedIn);
      case PointType.MultiWell:
        return this.calculateIconRadius('multi', zoomedIn);
      case PointType.MultiHouseHold:
        return this.calculateIconRadius('multi', zoomedIn);
      case PointType.DuctEndpoint:
        return this.calculateIconRadius('sdp', zoomedIn, true);
      case PointType.DuctPoint:
        return this.calculateIconRadius('default', zoomedIn);
      default:
        return null;
    }
  }

  calculateIconRadius(
    iconType: keyof typeof iconTypeSizes,
    zoomedIn: boolean,
    smallerOnZoom = false,
  ) {
    let radius = iconTypeSizes?.[iconType]?.[0];
    if (radius && smallerOnZoom && zoomedIn) {
      radius -= 8;
    }
    if (radius) {
      return this.calculateRadiusBasedOnZoom(radius, zoomedIn);
    } else {
      return null;
    }
  }

  calculateRadiusBasedOnZoom(radius: number, zoomedIn: boolean) {
    return zoomedIn ? radius : radius / 2;
  }

  centerView() {
    this.map.setView(
      this.mapBounds.getCenter(),
      this.map.getBoundsZoom(
        latLngBounds(
          this.mapBounds.getSouthWest(),
          this.mapBounds.getNorthEast(),
        ),
      ),
    );
  }

  onGeolocationFound(e: LocationEvent) {
    if (!this.gpsMarker) {
      this.gpsMarker = circle(e.latlng, {
        stroke: false,
        color: '#006EC3',
        fillOpacity: 0.2,
      }).addTo(this.map);
      this.map.panTo(latLng(this.gpsMarker.getLatLng()));
    }
    if (!this.gpsDotMarker) {
      this.gpsDotMarker = marker(e.latlng, {
        icon: this.geolocationImage,
      }).addTo(this.map);
    }
    this.gpsDotMarker.setLatLng(e.latlng);
    this.gpsMarker.setLatLng(e.latlng);
    this.gpsMarker.setRadius(e.accuracy);
  }

  onGeolocationError(e: ErrorEvent) {
    if (e.code === 1) {
      this.geolocationDenied.emit();
    }
    this.removeGeolocation();
  }

  addGeolocation() {
    this.showGeolocation = true;
    interval(1000)
      .pipe(takeUntil(this.cancelSub$))
      .subscribe(() => {
        this.map.locate();
      });
  }

  removeGeolocation() {
    this.showGeolocation = false;
    if (this.gpsMarker) {
      this.gpsMarker.remove();
      this.gpsMarker = null;
    }
    if (this.gpsDotMarker) {
      this.gpsDotMarker.remove();
      this.gpsDotMarker = null;
    }
    this.cancelSub$.next();
  }

  centerPosition() {
    if (this.gpsDotMarker) {
      this.map.setView(this.gpsDotMarker.getLatLng(), this.map.getZoom());
    }
  }

  onGeolocationClicked() {
    this.showGeolocation ? this.centerPosition() : this.addGeolocation();
  }

  showMapControls(show: boolean) {
    this.mapLoaded = show;
  }

  onSidePanelSelected(panel: SidePanel) {
    this.sidePanelSelected.emit(panel);
  }

  centerViewToObject(bounds: LatLngBounds) {
    if (!this.map) {
      return;
    }
    this.map.setView(
      bounds.getCenter(),
      this.map.getBoundsZoom(
        latLngBounds(bounds.getSouthWest(), bounds.getNorthEast()),
      ) < 18
        ? this.map.getBoundsZoom(
            latLngBounds(bounds.getSouthWest(), bounds.getNorthEast()),
          )
        : 18,
    );
  }

  private readonly handleMapInteractionForModal = () => {
    if (this.isMultiSpanHelpModalOpen()) {
      this.disableMapInteraction();
      return;
    }
    this.enableMapInteraction();
  };

  private disableMapInteraction() {
    this.map?.scrollWheelZoom?.disable?.();
    this.map?.dragging?.disable?.();
    this.map?.doubleClickZoom?.disable?.();
    this.map?.touchZoom?.disable?.();
  }

  private enableMapInteraction() {
    this.map?.scrollWheelZoom?.enable?.();
    this.map?.dragging?.enable?.();
    this.map?.doubleClickZoom?.enable?.();
    this.map?.touchZoom?.enable?.();
  }
}
